/**
 * Dialysis status + type (4.41 D2) — shared by the mobile Kidney Status save and
 * screen, and by the web Settings sheet and the two doctor reports. Pure, no
 * imports: the webapp reaches it by relative path, mobile via `@/shared/…`.
 *
 * The three scorers (`services/kidney/scoring.ts`, `webapp/src/kidney/scoring.ts`,
 * `functions/src/utils/scoring.ts`) and `functions/src/utils/buildContext.ts` keep
 * their own dialysis sets on purpose: the scorer triplet is parity-asserted and
 * `functions/` can't import `shared/`.
 *
 * The type steers labels and guidance — and, since 4.46 (F7a, M7a), the limits for ONE
 * value: "Starting soon" (`not_started`) on a dialysis status gets the Stage 5 (not on
 * dialysis) defaults with protein as a maximum (`calculateNutrientLimits` on both
 * platforms, the scorers' `effectiveCalculatedLimits`, `resolveDailyTargets`). The other
 * three types keep the dialysis defaults — 4.47 (M7b): peritoneal with potassium 3,000 mg.
 * A type change that crosses `not_started` or `peritoneal` (`dialysisTypeChangesLimits`)
 * rewrites `calculatedLimits` and keeps custom targets; past days keep their stored
 * targets (Rule 9).
 */

/**
 * Every stored condition that means "on dialysis". `on_dialysis` is what onboarding and
 * Settings write; `dialysis` comes from the web funnel; `ckd_stage_5_dialysis` is legacy.
 */
export const DIALYSIS_CONDITIONS: readonly string[] = ['on_dialysis', 'dialysis', 'ckd_stage_5_dialysis'];

/** The four onboarding step-9 values, in the order every picker shows them. */
export const DIALYSIS_TYPES = ['hemodialysis', 'peritoneal', 'home_hemo', 'not_started'] as const;

export type DialysisTypeValue = (typeof DIALYSIS_TYPES)[number];

export function isDialysisCondition(condition: string | null | undefined): boolean {
  return typeof condition === 'string' && DIALYSIS_CONDITIONS.includes(condition);
}

export function isDialysisType(value: unknown): value is DialysisTypeValue {
  return typeof value === 'string' && (DIALYSIS_TYPES as readonly string[]).includes(value);
}

/** 4.46 F7a: "Starting soon" — dialysis is planned, not started (Stage 5 limits, protein a maximum). */
export function isPreDialysisType(value: unknown): value is 'not_started' {
  return value === 'not_started';
}

/** 4.47 M7b: peritoneal dialysis — the dialysis defaults with potassium 3,000 mg (approved 2026-10-11). */
export function isPeritonealType(value: unknown): value is 'peritoneal' {
  return value === 'peritoneal';
}

/**
 * 4.47 M7b: does a change of the dialysis type move the calculated limits? On a dialysis
 * status the calculators have three rows: Starting soon (Stage 5), peritoneal (the dialysis
 * row with potassium 3,000 mg) and every other value, unset included (the dialysis row). A
 * change between two rows rewrites the whole `calculatedLimits` sub-map (Rule 7); custom
 * limits and targets are kept. `undefined → peritoneal` is a crossing.
 */
export function dialysisTypeChangesLimits(from: unknown, to: unknown): boolean {
  return isPreDialysisType(from) !== isPreDialysisType(to) || isPeritonealType(from) !== isPeritonealType(to);
}

/**
 * Label copy for each type — the onboarding step-9 keys, which exist in all 13 locales.
 * `fallback` is the English default for callers that pass one.
 */
export const DIALYSIS_TYPE_LABELS: Readonly<Record<DialysisTypeValue, { key: string; fallback: string }>> = {
  hemodialysis: { key: 'onboarding.questions.dialysisType.options.hemodialysis', fallback: 'Hemodialysis (HD)' },
  peritoneal: { key: 'onboarding.questions.dialysisType.options.peritoneal', fallback: 'Peritoneal dialysis (PD)' },
  home_hemo: { key: 'onboarding.questions.dialysisType.options.homeHemo', fallback: 'Home hemodialysis' },
  not_started: { key: 'onboarding.questions.dialysisType.options.notStarted', fallback: 'Starting soon' },
};

/** Unset or an unknown value: the report row just says "On Dialysis". */
export const DIALYSIS_TYPE_UNSET_LABEL = { key: 'kidneyStatus.stages.onDialysis', fallback: 'On Dialysis' } as const;

/** The report's "Dialysis" row value for a stored type (mobile PDF + web report). */
export function dialysisTypeLabel(value: unknown): { key: string; fallback: string } {
  return isDialysisType(value) ? DIALYSIS_TYPE_LABELS[value] : DIALYSIS_TYPE_UNSET_LABEL;
}
